import { AuthContext } from "components/authProvider/authProvider";
import { getAuth, signOut } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { app, host } from "../../config";
import CustomAlert from "components/generals/alert/CustomAlert";
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbar, useGridApiRef } from "@mui/x-data-grid";
import { Box, Button, Paper, Typography } from "@mui/material";
import { RenewalType } from "./types";

type Opportunity = {
  opportunityid: string;
  name: string;
  lvp_effectivedate: string;
};

const auth = getAuth();
const db = getFirestore(app);

const RenewalLetterDashboard: React.FC = () => {
  const apiRef = useGridApiRef();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);

  const [isLoading, setIsLoading] = useState(false);

  const [isAlertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const navigate = useNavigate();

  const openAlert = () => {
    setAlertOpen(true);
  };

  const closeAlert = () => {
    setAlertOpen(false);
  };

  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext is undefined");
  }
  const { currentUser, role, suspended } = authContext;

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      {
        field: "Renewal Letter",
        headerName: "Actions",
        minWidth: 200,
        maxWidth: 200,
        renderCell: (params: GridRenderCellParams<any>) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <strong>{params.value}</strong>
            <Button
              variant="contained"
              size="small"
              style={{ margin: "auto", backgroundColor: "#004e9d" }}
              onClick={() => handleOpen(params.row)}
            >
              Open Letter
            </Button>
          </Box>
        ),
        editable: false,
        headerClassName: "super-app-theme--header",
        headerAlign: "center",
      },
      {
        field: "name",
        headerName: "Name",
        editable: false,
        headerClassName: "super-app-theme--header",
        flex: 1,
        headerAlign: "center",
      },
      {
        field: "lvp_effectivedate",
        headerName: "Effective Date",
        editable: false,
        headerClassName: "super-app-theme--header",
        flex: 1,
        headerAlign: "center",
        align: "center",
        valueFormatter: (value: string) => {
          if (!value) {
            return "N/A";
          }
          return value.split("T")[0];
        },
      },
      {
        field: "Renewal_Type",
        headerName: "Renewal Type",
        editable: false,
        headerClassName: "super-app-theme--header",
        flex: 1,
        headerAlign: "center",
        valueFormatter: (value: RenewalType) => {
          console.log(value);
          return RenewalType[value];
        },
      },
    ];
    return columns;
  }, []);

  const fetchOpportunities = async () => {
    setIsLoading(true);
    const data = await currentUser.getIdToken(false).then((tk) => {
      const url = `${host}/api/implementation/renewalLetters`;
      return fetch(url, {
        headers: {
          authorization: `Bearer ${tk}`,
        },
      }).then((response: Response) => {
        if (response.ok) {
          setIsLoading(false);
          return response.json().then((res) => {
            return res.data;
          });
        } else {
          setAlertMessage(
            "There was an error when fetching submissions. Please try again later and contact IT if the problem persists."
          );
          setAlertOpen(true);
          return [];
        }
      });
    });

    setOpportunities(data);

    // requestAnimationFrame(() => {
    //   requestAnimationFrame(() => {
    //     apiRef.current?.autosizeColumns({
    //       includeHeaders: true,
    //       includeOutliers: true,
    //       expand: true,
    //       includeHeaderFilters: true,
    //     });
    //   });
    // });
  };

  if (suspended == true) {
    signOut(auth)
      .then(() => {
        console.log("Signed out");
        return <Navigate to="/login" />;
      })
      .catch((error) => {
        console.error("Error signing out", error);
      });
  }

  if (!currentUser) {
    return <Navigate to="/login" />;
  }

  if (role !== "admin" && role !== "internal_ugp") {
    return <Navigate to="/basePage" />;
  }

  function handleOpen(row: any): void {
    navigate("/renewalLetter/" + row.opportunityid);
  }

  return (
    <div className={"myProj-body"}>
      <CustomAlert message={alertMessage} isOpen={isAlertOpen} onClose={closeAlert} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "100%",
          width: "85vw",
          my: 4,
          mx: "auto",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 4,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
            width: "100%",
            height: "100%",
            textAlign: "center",
          }}
        >
          <Typography variant="h4" sx={{ mb: 2 }}>
            Renewal Letters
          </Typography>
          <Box
            sx={{
              height: 650,
              width: "80vw", // Updated to 100%
              backgroundColor: "#f8f8f8",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)", // Enhanced shadow
              padding: "20px",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              "& .MuiDataGrid-columnHeaders .super-app-theme--header": {
                backgroundColor: "#004e9d",
                color: "#ffffff",
                padding: "10px 20px",
                boxShadow: "0 3px 6px rgba(0,0,0,0.2)", // Enhanced shadow
                fontWeight: "bold",
              },
              "& .MuiDataGrid-root": {
                border: "none",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                color: "#333",
              },
            }}
          >
            <DataGrid
              sx={{
                height: "auto", // Allows DataGrid to grow with content up to the Box height
                width: "100%", // Ensures DataGrid uses full width of the Box, considering padding
                "& .MuiDataGrid-virtualScroller": {
                  marginTop: "0 !important", // Adjusts the header alignment issue
                },
                "& .MuiDataGrid-iconButtonContainer": {
                  marginLeft: "5px",
                },
              }}
              apiRef={apiRef}
              rows={opportunities}
              columns={columns}
              getRowId={(row) => row.opportunityid}
              disableColumnSelector
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
              }}
              pageSizeOptions={[5, 10, 50, 100]}
              slots={{
                toolbar: GridToolbar,
              }}
              showToolbar
              disableRowSelectionOnClick
              loading={isLoading}
            />
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default RenewalLetterDashboard;
